import get from 'lodash/get'
import * as React from 'react'
import { useDrop } from 'react-dnd'

import {
  TRACK_PLAYLISTED_FROM_SIM_SEARCH,
  TRACK_PLAYLISTED_FROM_SIM_TAB,
} from '@disco/constants/EventTypes'
import { INBOX_TYPES } from '@disco/constants/GroupTypes'
import { FILE, PLAYLIST, SECTION, TRACK } from '@disco/constants/ItemTypes'
import localProvider from '@disco/services/upload/providers/local'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { resetSelection } from '@disco/state-management/actions/SelectionActions'
import {
  addShortlistSection,
  insertAggregatedTrack,
  insertExternalPlaylistTracks,
  insertExternalTrackBatch,
  insertItem,
  insertPlaylist,
  insertSelectedItems,
  moveSelectedItems,
  removeShortlistSection,
  updateShortlistSection,
} from '@disco/state-management/actions/ShortlistActions'
import { getGroupsFromExternalShare } from '@disco/state-management/selectors/groups'
import {
  getAllSelectedItems,
  getShortlistsSelection,
} from '@disco/state-management/selectors/selection'
import {
  getShortlistItems,
  getShortlistSections,
} from '@disco/state-management/selectors/shortlists'
import {
  sendPlaylistDownloadEventThrottled,
  sendTrackDownloadEventThrottled,
} from '@disco/utils/eventsApi'
import { getFilesFromDraggedItem } from '@disco/utils/file-utils'
import { getMixpanelDragAndDropProps } from '@disco/utils/mixpanel/drop-tracks'
import { pushUINotification } from '@disco/utils/notifications'
import { isDraggedItemPartOfSelection } from '@disco/utils/selection'
import {
  getIndexWithoutSections,
  getItemsWithSections,
} from '@disco/utils/shortlist'

import type { GroupId } from 'types/group'
import type { Playlist } from 'types/playlist'
import type { SelectableItem } from 'types/selection'
import type { ShortlistSection } from 'types/shortlist'
import type { Track } from 'types/track'

import { useDispatch, useSelector } from 'hooks/use-redux'

import type {
  DraggedItem,
  ShortlistDropProps,
  ShortlistItem,
  ShortlistMonitor,
} from './types'

const getDraggedTracks = (
  draggedItem: DraggedItem,
  selectedItems: SelectableItem[],
): Array<number | string> => {
  const partOfSelection = isDraggedItemPartOfSelection(
    selectedItems,
    draggedItem,
  )
  if (partOfSelection) {
    return selectedItems.map(item => item.id as number | string)
  }
  if (draggedItem?.track) {
    return [draggedItem.track.id]
  }
  return []
}

const getTargetSection = (
  index: number,
  items: ShortlistItem[],
): ShortlistSection | undefined => {
  const normalizedIndex = Math.min(index, items.length - 1)
  const item = items[normalizedIndex]

  if (!item) {
    return undefined
  }

  if (!('track' in item)) {
    return item
  }

  return undefined
}

export const shortlistTarget = {
  drop(
    props: ShortlistDropProps,
    monitor: ShortlistMonitor,
    _component?: unknown,
  ) {
    const { items, sections, selectedItems, externalSharedGroups } = props
    const draggedItem = monitor.getDropResult()
    if (!draggedItem || draggedItem.alreadyHandled) return props

    const draggedTracks = getDraggedTracks(draggedItem, selectedItems)

    const isNotDragged = (item: ShortlistItem): boolean => {
      if ('track' in item && item.track) {
        return !draggedTracks.includes(item.track.id)
      }
      return item.id !== draggedItem.section?.id
    }

    const isSourceGroupExternal = (originId: GroupId | undefined) =>
      externalSharedGroups.some(group => group.id === originId)

    const itemsWithSections = getItemsWithSections(items, sections).filter(
      isNotDragged,
    )
    const position = getIndexWithoutSections(
      draggedItem.index,
      itemsWithSections,
    )
    const targetSection = getTargetSection(
      draggedItem.index != null ? draggedItem.index - 1 : -1,
      itemsWithSections,
    )

    if (draggedItem.section) {
      const { shortlistId } = props
      const { section, shortlistId: sourceShortlistId } = draggedItem

      if (shortlistId === sourceShortlistId) {
        const sectionTargetIndex =
          draggedItem.index != null
            ? itemsWithSections
                .slice(0, draggedItem.index)
                .filter((item: ShortlistItem) => !('track' in item)).length
            : undefined
        props.updateShortlistSection(
          shortlistId,
          section.id,
          section.name,
          position,
          sectionTargetIndex,
        )
      } else {
        props.addShortlistSection(shortlistId, section.name, position)
        props.removeShortlistSection(sourceShortlistId!, section.id)
      }
      return
    }

    if (draggedItem.items) {
      getFilesFromDraggedItem({ items: draggedItem.items }).then(
        (files: File[]) => {
          if (files.length > 0) {
            props.uploadFiles(
              localProvider,
              props.shortlistId,
              files,
              pushUINotification,
              { positionId: position, targetSection, wasDragged: true },
            )
            props.sendMixpanelEvent('Playlist Creator - Drag and drop files', {
              Count: files.length,
            })
          }
        },
      )
      return
    }

    const partOfSelection = isDraggedItemPartOfSelection(
      selectedItems,
      draggedItem,
    )

    const currentShortlistSelection = get(
      props.shortlistsSelection[props.shortlistId],
      'items',
      null,
    )
    const mixpanelProperties = getMixpanelDragAndDropProps(
      draggedItem,
      'Playlist creator',
    )

    if (currentShortlistSelection && currentShortlistSelection.length > 0) {
      props.moveSelectedItems(
        props.shortlistId,
        currentShortlistSelection,
        position,
        targetSection,
      )
      props.resetSelection()
      return
    }

    if (selectedItems.length > 0 && partOfSelection) {
      if (
        draggedItem.sourceGroupType != null &&
        INBOX_TYPES.includes(draggedItem.sourceGroupType)
      ) {
        selectedItems.forEach(item => {
          if (item.type === PLAYLIST) {
            sendPlaylistDownloadEventThrottled(
              (item as Playlist).playlist_id,
              undefined,
              draggedItem.sourceGroupId,
            )
          } else if (item.type === TRACK) {
            sendTrackDownloadEventThrottled((item as Track).id, 'in-app', {
              group_id: draggedItem.sourceGroupId,
              playlist_id: draggedItem.sourcePlaylistId,
            })
          }
        })
      }

      if (isSourceGroupExternal(draggedItem.sourceGroupId)) {
        props.insertExternalTrackBatch(
          props.shortlistId,
          selectedItems as (Playlist | Track)[],
          draggedItem.sourceGroupId!,
          position,
          targetSection,
        )
      } else {
        props.insertSelectedItems(
          props.shortlistId,
          selectedItems,
          position,
          targetSection,
        )
      }
      props.resetSelection()
      props.sendMixpanelEvent(
        'Drag and drop selected items',
        mixpanelProperties,
      )
      return
    }

    if (draggedItem.track) {
      if (!draggedItem.track?.permissions?.manage) {
        props.insertAggregatedTrack(
          props.shortlistId,
          draggedItem.track,
          position,
          targetSection,
        )
      } else {
        props.insertItem(
          props.shortlistId,
          draggedItem as { track: Track },
          position,
          targetSection,
        )
      }

      const internal_data = draggedItem.track.internal_data
      props.sendMixpanelEvent(
        'Drag and drop existing track',
        mixpanelProperties,
      )

      if (internal_data?.[TRACK_PLAYLISTED_FROM_SIM_TAB]) {
        props.sendMixpanelEvent(
          'Track playlisted from Track Panel Similar Tracks',
          {
            'Track panel': internal_data[TRACK_PLAYLISTED_FROM_SIM_TAB].panelId,
            'Track playlisted': draggedItem.track.id,
          },
        )
      }

      if (internal_data?.[TRACK_PLAYLISTED_FROM_SIM_SEARCH]) {
        props.sendMixpanelEvent(
          'Track playlisted from Similarity Search by URL',
          {
            ...mixpanelProperties,
            URL: internal_data[TRACK_PLAYLISTED_FROM_SIM_SEARCH].url,
            'Track playlisted': draggedItem.track.id,
          },
        )
      }

      if (
        draggedItem.sourceGroupType != null &&
        INBOX_TYPES.includes(draggedItem.sourceGroupType)
      ) {
        sendTrackDownloadEventThrottled(draggedItem.track.id, 'in-app', {
          group_id: draggedItem.sourceGroupId,
          playlist_id: draggedItem.sourcePlaylistId,
        })
      }
    }

    const isDiscoverItem = draggedItem?.id?.includes('discover')
    if (draggedItem.playlist && !isDiscoverItem) {
      if (!draggedItem.playlist?.permissions?.manage) {
        props.insertExternalPlaylistTracks(
          props.shortlistId,
          draggedItem.playlist,
          position,
          targetSection,
          draggedItem.sourceGroupId,
        )
      } else {
        props.insertPlaylist(
          props.shortlistId,
          draggedItem as { playlist: Playlist },
          position,
          targetSection,
        )
      }
      props.sendMixpanelEvent('Drag and drop existing playlist')

      if (
        draggedItem.sourceGroupType != null &&
        INBOX_TYPES.includes(draggedItem.sourceGroupType)
      ) {
        sendPlaylistDownloadEventThrottled(
          draggedItem.playlist.playlist_id,
          undefined,
          draggedItem.sourceGroupId,
        )
      }
    }

    props.resetSelection()
  },
}

type Props = {
  shortlistId: string
  uploadFiles: (...args: any[]) => any
  children: React.ReactNode
}

export default function ShortlistDropWrapper({
  shortlistId,
  uploadFiles,
  children,
}: Props) {
  const dispatch = useDispatch()
  const items = useSelector(state => getShortlistItems(state, { shortlistId }))
  const sections = useSelector(state =>
    getShortlistSections(state, { shortlistId }),
  )
  const selectedItems = useSelector(getAllSelectedItems)
  const shortlistsSelection = useSelector(getShortlistsSelection)
  const externalSharedGroups = useSelector(getGroupsFromExternalShare)

  const [, dropRef] = useDrop(
    () => ({
      accept: [FILE, TRACK, PLAYLIST, SECTION],
      drop: (_item: unknown, monitor) => {
        shortlistTarget.drop(
          {
            shortlistId,
            items,
            sections,
            selectedItems,
            shortlistsSelection,
            externalSharedGroups,
            uploadFiles,
            updateShortlistSection: (id, sectionId, name, position, index) =>
              dispatch(
                updateShortlistSection(id, sectionId, name, position, index),
              ),
            addShortlistSection: (id, name, position) =>
              dispatch(addShortlistSection(id, name, position)),
            removeShortlistSection: (id, sectionId) =>
              dispatch(removeShortlistSection(id, sectionId)),
            insertItem: (id, item, positionId, targetSection) =>
              dispatch(insertItem(id, item, positionId, targetSection)),
            insertAggregatedTrack: (id, track, positionId, targetSection) =>
              dispatch(
                insertAggregatedTrack(id, track, positionId, targetSection),
              ),
            insertSelectedItems: (id, sel, positionId, targetSection) =>
              dispatch(insertSelectedItems(id, sel, positionId, targetSection)),
            moveSelectedItems: (id, indexes, positionId, targetSection) =>
              positionId != null &&
              dispatch(
                moveSelectedItems(id, indexes, positionId, targetSection!),
              ),
            insertPlaylist: (id, item, positionId, targetSection) =>
              dispatch(insertPlaylist(id, item, positionId, targetSection)),
            insertExternalTrackBatch: (
              id,
              sel,
              sourceGroupId,
              positionId,
              targetSection,
            ) =>
              dispatch(
                insertExternalTrackBatch(
                  id,
                  sel,
                  sourceGroupId,
                  positionId,
                  targetSection,
                ),
              ),
            insertExternalPlaylistTracks: (
              id,
              playlist,
              positionId,
              targetSection,
              sourceGroupId,
            ) =>
              dispatch(
                insertExternalPlaylistTracks(
                  id,
                  playlist,
                  positionId,
                  targetSection,
                  sourceGroupId,
                ),
              ),
            resetSelection: () => dispatch(resetSelection()),
            sendMixpanelEvent: (name, props) =>
              dispatch(sendMixpanelEvent(name, props)),
          },
          monitor,
        )
      },
    }),
    [
      dispatch,
      shortlistId,
      items,
      sections,
      selectedItems,
      shortlistsSelection,
      externalSharedGroups,
      uploadFiles,
    ],
  )

  return <div ref={dropRef}>{children}</div>
}
